import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import MarketingRequestApi from "../../../api/MarketingRequestApi";

const TOTAL_STEPS = 4;

const SERVICE_OPTIONS = [
    "종합 마케팅",
    "네이버 마케팅",
    "SNS 마케팅",
    "구글 마케팅",
    "플레이스",
    "스토어",
    "쿠팡",
    "블로그",
    "SEO",
    "유튜브",
    "기타",
];

const CONTACT_OPTIONS = [
    { value: "phone", label: "전화번호", placeholder: "010-0000-0000", fieldLabel: "전화번호" },
    { value: "sms", label: "문자", placeholder: "010-0000-0000", fieldLabel: "문자 번호" },
    { value: "kakao", label: "카카오톡", placeholder: "카카오톡 ID 입력", fieldLabel: "카카오톡 ID" },
];

const BUDGET_OPTIONS = [
    "상담 후 결정",
    "50만원",
    "50~100만원",
    "100~200만원",
    "200~500만원",
    "500만원 이상",
];

const STEP_PROGRESS = {
    1: { value: 25, label: "1STEP" },
    2: { value: 50, label: "2STEP" },
    3: { value: 75, label: "3STEP" },
    4: { value: 100, label: "4STEP" },
};

const initialFormData = {
    services: [],
    comment: "",
    contactMethod: "phone",
    contactPhone: "",
    contactSms: "",
    contactKakao: "",
    budget: "",
};

const MarketingCompareExplain = ({ handleNext }) => {
    const [isAgree, setIsAgree] = useState(false);
    const clickStart = () => {
        if(!isAgree) return;
        handleNext();
    }
    return (
        <div className="extimate-container">
            <div className="extimate-header">
                <span>무료 견적 비교</span>
                <h1>
                    여러 마케팅 실행사가<br />
                    <strong><span className="primary">직접 견적</span>을 보내드려요</strong>
                </h1>
                <p>
                    의뢰 한 번에 검증된 업체들의 견적이 모입니다.<br />
                    비교하고 선택하면 광고비, 확실히 아낄 수 있어요.
                </p>
            </div>
            <ul className="extimate-list">
                <li>
                    <img src="/static/img/assets/extimate_01.png" />
                    <div className="extimate-list-text">
                        <strong>검증된 마케터</strong>
                        <span>만 등록</span>
                    </div>
                </li>
                <li>
                    <img src="/static/img/assets/extimate_02.png" />
                    <div className="extimate-list-text">
                        <span>견적 비교 </span>
                        <strong>무료</strong>
                    </div>
                </li>
                <li>
                    <img src="/static/img/assets/extimate_03.png" />
                    <div className="extimate-list-text">
                        <span> 평균 광고비 절감 </span>
                        <strong>30%</strong>
                    </div>
                </li>
                <li>
                    <img src="/static/img/assets/extimate_04.png" />
                    <div className="extimate-list-text">
                        <span>마음에 안들면 </span>
                        <strong>다시 비교</strong>
                    </div>
                </li>
            </ul>

            <div className="extimate-apply-form">
                <label className="checkbox">
                    <input type="checkbox" checked={isAgree} onChange={(e) => setIsAgree(e.target.checked)} />
                    <span>개인정보수집동의(필수)</span>
                </label>

                <button type="submit" className="btn btn-primary btn-shadow" disabled={!isAgree} onClick={clickStart}>
                    <span>무료 견적 받기</span>
                </button>
            </div>
        </div>
    )
}

const MarketingCompare = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [formData, setFormData] = useState(initialFormData);
    const [selectedServices, setSelectedServices] = useState([]);
    const [leavingServices, setLeavingServices] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const progress = STEP_PROGRESS[step];

    const getContactValue = () => {
        if (formData.contactMethod === "phone") return formData.contactPhone;
        if (formData.contactMethod === "sms") return formData.contactSms;
        return formData.contactKakao;
    };

    const isNextDisabled = () => {
        if (step === 1) return selectedServices.length === 0;
        if (step === 2) return formData.comment.trim().length === 0;
        if (step === 3) return getContactValue().trim().length === 0;
        if (step === 4) return !formData.budget;
        return false;
    };

    const handleServiceToggle = (value, checked) => {
        if (checked) {
            setSelectedServices((prev) => (prev.includes(value) ? prev : [...prev, value]));
            setFormData((prev) => ({
                ...prev,
                services: prev.services.includes(value)
                    ? prev.services
                    : [...prev.services, value],
            }));
            return;
        }

        setLeavingServices((prev) => [...prev, value]);
        setTimeout(() => {
            setSelectedServices((prev) => prev.filter((v) => v !== value));
            setLeavingServices((prev) => prev.filter((v) => v !== value));
            setFormData((prev) => ({
                ...prev,
                services: prev.services.filter((v) => v !== value),
            }));
        }, 200);
    };

    const handleRemoveSelectedService = (value) => {
        handleServiceToggle(value, false);
    };

    const handleContactMethodChange = (method) => {
        setFormData((prev) => ({ ...prev, contactMethod: method }));
    };

    const handleContactValueChange = (method, value) => {
        if (method === "phone") {
            setFormData((prev) => ({ ...prev, contactPhone: value }));
        } else if (method === "sms") {
            setFormData((prev) => ({ ...prev, contactSms: value }));
        } else {
            setFormData((prev) => ({ ...prev, contactKakao: value }));
        }
    };

    const submitEstimate = useCallback(async () => {
        setIsSubmitting(true);
        try {
            await MarketingRequestApi.createRequest(formData);
            setStep(5);
        } catch (error) {
            console.error("견적 요청 저장 실패:", error);
            alert("견적 요청 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.");
        } finally {
            setIsSubmitting(false);
        }
    }, [formData]);

    const handleNext = () => {
        if (isNextDisabled() || isSubmitting) return;
        if (step === 4) {
            submitEstimate();
            return;
        }
        setStep((prev) => prev + 1);
    };

    const handlePrev = () => {
        if (step <= 0) {
            navigate("/");
            return;
        }
        setStep((prev) => prev - 1);
    };

    useEffect(() => {
        if (step !== 1) return;
        setFormData((prev) => ({ ...prev, services: selectedServices }));
    }, [selectedServices, step]);

    if (step === 0) {
        return <MarketingCompareExplain handleNext={handleNext} />
    }

    if (step === 5) {
        return (
            <div className="extimate-step-container">
                <div className="extimate-success">
                    <div className="extimate-success-img">
                        <img
                            src="/static/img/assets/extimate_success.png"
                            alt="견적 요청 완료"
                        />
                    </div>
                    <h2>
                        견적요청이
                        <br />
                        <span className="primary">완료</span>되었습니다
                    </h2>
                    <p>마케팅전문가들이 곧 견적을 보내드려요!</p>
                </div>
                <div className="extimate-button-area">
                    <Link to="/" className="btn btn-primary btn-shadow">
                        <span>메인 화면으로 이동</span>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="extimate-step-container">
            <h2>마케팅 비교견적</h2>
            <div className="extimate-step-progress-bar">
                <progress value={progress.value} max="100"></progress>
                <span>{progress.label}</span>
            </div>

            {step === 1 && (
                <>
                    <h3>어떤 마케팅이 필요하세요?</h3>
                    <ul className="extimate-selected-list">
                        {selectedServices.map((value) => (
                            <li
                                key={value}
                                data-value={value}
                                className={leavingServices.includes(value) ? "leave" : ""}
                            >
                                <button
                                    type="button"
                                    onClick={() => handleRemoveSelectedService(value)}
                                    aria-label={`${value} 선택 해제`}
                                >
                                    {value}
                                </button>
                            </li>
                        ))}
                    </ul>
                    <ul className="extimate-selector-list">
                        {SERVICE_OPTIONS.map((value) => {
                            const isSelected = selectedServices.includes(value);
                            return (
                                <li
                                    key={value}
                                    className={isSelected ? "active" : ""}
                                    onClick={() => handleServiceToggle(value, !isSelected)}
                                >
                                    <span className="checkbox">
                                        <input
                                            type="checkbox"
                                            name="estimate-service"
                                            value={value}
                                            checked={isSelected}
                                            readOnly
                                            tabIndex={-1}
                                        />
                                        <span>{value}</span>
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </>
            )}

            {step === 2 && (
                <>
                    <h3>의뢰내용(요청사항)을 알려주세요!</h3>
                    <div className="extimate-description">
                        <p>자세할수록 정확한 견적이 와요</p>
                    </div>
                    <div className="extimate-comments">
                        <textarea
                            id="estimateComment"
                            name="estimateComment"
                            placeholder={
                                "예) 강남구 카페인데 플레이스 노출이 안 되고 있어요.\n리뷰는 30개 있고 월 예산은 30만원 생각하고 있습니다."
                            }
                            value={formData.comment}
                            onChange={(e) =>
                                setFormData((prev) => ({ ...prev, comment: e.target.value }))
                            }
                        />
                    </div>
                </>
            )}

            {step === 3 && (
                <>
                    <h3>선호하는 연락수단을 알려주세요!</h3>
                    <div className="extimate-description">
                        <p>※ 연락처는 견적 제출한 마케터에게만 공개됩니다.</p>
                    </div>
                    <div className="extimate-selector-list">
                        <ul>
                            {CONTACT_OPTIONS.map((option) => (
                                <li key={option.value}>
                                    <label className="checkbox">
                                        <input
                                            type="radio"
                                            name="estimate-contact"
                                            value={option.label}
                                            data-target={option.value}
                                            checked={formData.contactMethod === option.value}
                                            onChange={() =>
                                                handleContactMethodChange(option.value)
                                            }
                                        />
                                        <span>{option.label}</span>
                                    </label>
                                </li>
                            ))}
                        </ul>
                        {CONTACT_OPTIONS.map((option) => {
                            const fieldValue =
                                option.value === "phone"
                                    ? formData.contactPhone
                                    : option.value === "sms"
                                    ? formData.contactSms
                                    : formData.contactKakao;
                            return (
                                <div
                                    key={option.value}
                                    className="extimate-selector-field"
                                    data-field={option.value}
                                    hidden={formData.contactMethod !== option.value}
                                >
                                    <label htmlFor={`estimate-${option.value}`}>
                                        {option.fieldLabel}
                                    </label>
                                    <input
                                        type="text"
                                        id={`estimate-${option.value}`}
                                        name={`estimate-${option.value}`}
                                        placeholder={option.placeholder}
                                        value={fieldValue}
                                        onChange={(e) =>
                                            handleContactValueChange(option.value, e.target.value)
                                        }
                                    />
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {step === 4 && (
                <>
                    <h3>예산범위</h3>
                    <ul className="extimate-selector-list">
                        {BUDGET_OPTIONS.map((value) => (
                            <li key={value}>
                                <label className="checkbox">
                                    <input
                                        type="radio"
                                        name="estimate-budget"
                                        value={value}
                                        checked={formData.budget === value}
                                        onChange={() =>
                                            setFormData((prev) => ({ ...prev, budget: value }))
                                        }
                                    />
                                    <span>{value}</span>
                                </label>
                            </li>
                        ))}
                    </ul>
                </>
            )}

            <div className="extimate-button-area">
                <button type="button" className="btn" onClick={handlePrev}>
                    <span>이전</span>
                </button>
                <button
                    type="button"
                    className="btn btn-primary btn-shadow"
                    disabled={isNextDisabled() || isSubmitting}
                    onClick={handleNext}
                >
                    <span>{step === 4 ? "신청하기" : "다음"}</span>
                </button>
            </div>
        </div>
    );
};

export default MarketingCompare;
